function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);
}

export class GoogleAccountController {
  constructor() {
    this.user = null;
    this.error = null;
    this.slots = [...document.querySelectorAll('[data-google-account]')];
    this.bindEvents();
    this.loadUser();
  }

  bindEvents() {
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-google-account-action]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        this.handleAction(trigger.dataset.googleAccountAction, trigger.closest('[data-google-account]'));
        return;
      }

      if (!event.target.closest('.google-account-menu')) this.closeMenus();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.closeMenus();
    });
    window.addEventListener('google-account-changed', event => {
      this.setUser(event.detail?.user ?? null);
    });
  }

  async loadUser() {
    try {
      const response = await fetch('/api/auth/me', {
        cache: 'no-store',
        credentials: 'same-origin'
      });
      const result = await response.json();
      if (!response.ok || !Object.hasOwn(result, 'user')) {
        throw new Error(result.error || `Không thể tải tài khoản (${response.status}).`);
      }
      this.setUser(result.user || null);
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Không thể tải thông tin tài khoản.';
      console.error('[Google account] Failed to load current user:', error);
      this.render();
    }
  }

  setUser(user) {
    this.user = user;
    this.error = null;
    this.render();
  }

  render() {
    for (const slot of this.slots) {
      const variant = slot.dataset.variant || 'avatar';
      if (this.error) {
        slot.innerHTML = `
          <button class="google-account-signin is-unavailable" type="button" disabled title="${escapeHtml(this.error)}">
            Tài khoản
          </button>
        `;
        continue;
      }

      if (!this.user) {
        slot.innerHTML = variant === 'workspace'
          ? '<button class="google-account-signin" type="button" data-google-account-action="login">Đăng nhập</button>'
          : '<button class="google-account-signin" type="button" data-google-account-action="login" aria-label="Đăng nhập Google">Đăng nhập</button>';
        continue;
      }

      const displayName = escapeHtml(this.user.displayName || this.user.email || 'Tài khoản Google');
      const avatar = this.user.avatarUrl
        ? `<img src="${escapeHtml(this.user.avatarUrl)}" alt="" referrerpolicy="no-referrer" />`
        : `<span>${escapeHtml((this.user.displayName || this.user.email || 'G').slice(0, 1).toUpperCase())}</span>`;
      slot.innerHTML = `
        <div class="google-account-menu">
          <button class="google-account-avatar" type="button" data-google-account-action="toggle" aria-label="Tài khoản Google: ${displayName}" aria-expanded="false">
            ${avatar}
          </button>
          <section class="google-account-popover" hidden>
            <div class="google-account-popover-avatar">${avatar}</div>
            <strong>${displayName}</strong>
            <span>${escapeHtml(this.user.email || '')}</span>
            ${this.user.isAdmin ? '<button type="button" class="google-account-admin-btn" data-google-account-action="admin">Trang quản trị</button>' : ''}
            <button type="button" data-google-account-action="logout">Đăng xuất</button>
            <p class="google-account-error" role="alert"></p>
          </section>
        </div>
      `;
    }
  }

  closeMenus() {
    for (const menu of document.querySelectorAll('.google-account-menu.is-open')) {
      menu.classList.remove('is-open');
      menu.querySelector('[data-google-account-action="toggle"]')?.setAttribute('aria-expanded', 'false');
      const popover = menu.querySelector('.google-account-popover');
      if (popover) popover.hidden = true;
    }
  }

  async handleAction(action, slot) {
    if (action === 'login') {
      const returnTo = window.location.hash || '#/home';
      window.location.assign(`/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }

    if (action === 'toggle') {
      const menu = slot?.querySelector('.google-account-menu');
      const open = menu && !menu.classList.contains('is-open');
      this.closeMenus();
      if (open) {
        menu.classList.add('is-open');
        menu.querySelector('[data-google-account-action="toggle"]')?.setAttribute('aria-expanded', 'true');
        const popover = menu.querySelector('.google-account-popover');
        if (popover) popover.hidden = false;
      }
      return;
    }

    if (action === 'admin') {
      this.closeMenus();
      window.location.hash = '#/admin';
      return;
    }

    if (action !== 'logout') return;
    const button = slot?.querySelector('[data-google-account-action="logout"]');
    if (button) button.disabled = true;
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        cache: 'no-store',
        credentials: 'same-origin'
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `Đăng xuất thất bại (${response.status}).`);
      this.setUser(null);
      window.dispatchEvent(new CustomEvent('google-account-changed', { detail: { user: null } }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không thể đăng xuất.';
      console.error('[Google account] Logout failed:', error);
      const messageSlot = slot?.querySelector('.google-account-error');
      if (messageSlot) messageSlot.textContent = message;
    } finally {
      const currentButton = slot?.querySelector('[data-google-account-action="logout"]');
      if (currentButton) currentButton.disabled = false;
    }
  }
}
